import axiosInstance from './axiosCreator';
import { store } from '../store/store';
import {
  pendingFeedbackMilestoneSet,
  todaySessionUpdated,
} from '../slices/todaySession/todaySessionSlice';
import {
  qbankProgressReceived,
  type QbankProgressSnapshot,
} from '../slices/qbankProgress/qbankProgressSlice';
import type { StudySession } from '../types/Study';

export interface StudySessionFlashcardResponse {
  id: string;
  response: number;
  flashcardId: string;
  flashcardVariantId: string;
  studySessionId: string | null;
  submissionDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export const updateStudySession = async (
  {
    id,
    date,
    isComplete,
    phase,
    isStarted,
    isUsingFlashcards,
    isUsingQbank,
    activeBlockId,
    vignetteCount,
    vignetteCorrectCount,
    vignetteIncorrectCount,
    vignetteOmittedCount,
    flashcardCount,
    completedFlashcardCount,
    isSnoozed,
    studiedAnywayOnOffDay,
    isDemo,
    hasSeenPriorities,
    isFirstSession,
  }: {
    id: string;
    date?: string;
    isComplete?: boolean;
    phase?: string;
    isStarted?: boolean;
    isUsingFlashcards?: boolean;
    isUsingQbank?: boolean;
    activeBlockId?: string;
    vignetteCount?: number;
    vignetteCorrectCount?: number;
    vignetteIncorrectCount?: number;
    vignetteOmittedCount?: number;
    flashcardCount?: number;
    completedFlashcardCount?: number;
    isSnoozed?: boolean;
    studiedAnywayOnOffDay?: boolean;
    isDemo?: boolean;
    hasSeenPriorities?: boolean;
    isFirstSession?: boolean;
  },
  options?: { shouldAccept?: (session: StudySession) => boolean }
) => {
  try {
    const response = await axiosInstance.patch(`/api/v5/study/session/${id}`, {
      ...(date !== undefined && { date }),
      ...(isComplete !== undefined && { isComplete }),
      ...(phase !== undefined && { phase }),
      ...(isStarted !== undefined && { isStarted }),
      ...(isUsingFlashcards !== undefined && { isUsingFlashcards }),
      ...(isUsingQbank !== undefined && { isUsingQbank }),
      ...(activeBlockId !== undefined && { activeBlockId }),
      ...(vignetteCount !== undefined && { vignetteCount }),
      ...(vignetteCorrectCount !== undefined && { vignetteCorrectCount }),
      ...(vignetteIncorrectCount !== undefined && { vignetteIncorrectCount }),
      ...(vignetteOmittedCount !== undefined && { vignetteOmittedCount }),
      ...(flashcardCount !== undefined && { flashcardCount }),
      ...(completedFlashcardCount !== undefined && { completedFlashcardCount }),
      ...(isSnoozed !== undefined && { isSnoozed }),
      ...(studiedAnywayOnOffDay !== undefined && { studiedAnywayOnOffDay }),
      ...(isDemo !== undefined && { isDemo }),
      ...(hasSeenPriorities !== undefined && { hasSeenPriorities }),
      ...(isFirstSession !== undefined && { isFirstSession }),
    });

    const updatedSession: StudySession = response.data?.data ?? response.data;
    if (options?.shouldAccept && !options.shouldAccept(updatedSession)) {
      return undefined;
    }

    store.dispatch(todaySessionUpdated(updatedSession));

    // On completion the backend may flag a newly-reached session-count milestone
    // (SESSIONS_5/20/50); hand it to SessionFeedbackProvider to prompt for
    // EXPERIENCE feedback (ORA-196). Absent on older backends → no-op.
    const pendingFeedbackMilestone = response.data?.pendingFeedbackMilestone;
    if (pendingFeedbackMilestone) {
      store.dispatch(pendingFeedbackMilestoneSet(pendingFeedbackMilestone));
    }

    return updatedSession;
  } catch (error) {
    console.error(error);
  }
};

// Get flashcardResponses for a studySession
export const fetchFlashcardResponses = async (sessionId: string) => {
  try {
    const response = await axiosInstance.get(
      `/studySession/${sessionId}/flashcardResponses`
    );

    if (Array.isArray(response.data)) {
      return response.data as StudySessionFlashcardResponse[];
    }

    if (Array.isArray(response.data?.data)) {
      return response.data.data as StudySessionFlashcardResponse[];
    }

    return [] as StudySessionFlashcardResponse[];
  } catch (error) {
    console.error(error);
    return [] as StudySessionFlashcardResponse[];
  }
};

// Get the first session of an exam for current user
export const fetchFirstStudySession = async (examId: string) => {
  try {
    const response = await axiosInstance.get(
      `/studySession/${examId}/firstExamSession`
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchQbankProgress = async (
  sessionId: string
): Promise<QbankProgressSnapshot | undefined> => {
  try {
    const response = await axiosInstance.get(
      `/api/v5/study/session/${sessionId}/qbank-progress`
    );

    const progress = response.data?.data as QbankProgressSnapshot | undefined;
    if (progress) {
      store.dispatch(
        qbankProgressReceived({ studySessionId: sessionId, progress })
      );
    }
    return progress;
  } catch (error) {
    console.error(error);
  }
};
